import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/projeto.css'; // Reusing some styles
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const AcaoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    nome: '',
    valor_destinado: '',
    data_inicio: '',
    data_termino: '',
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);

  const token = localStorage.getItem('accessToken');
  let canCreateAcoes = false;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      canCreateAcoes = decoded.cria_acoes === true;
    } catch (e) {}
  }

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/acao');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão ou permissão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
  }, [verificarAPI]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'ação', { id, dadosAtualizacao });
    try {
      const dadosValidos = { ...dadosAtualizacao };
      if (dadosValidos.hasOwnProperty('valor_destinado')) {
        const valorString = String(dadosValidos.valor_destinado);
        if (valorString === '' || dadosValidos.valor_destinado === null || valorString === 'null') {
          dadosValidos.valor_destinado = null;
        } else if (valorString.includes(',')) {
          // Valor formatado brasileiro (ex: 1.000,00)
          dadosValidos.valor_destinado = parseFloat(valorString.replace(/\./g, '').replace(',', '.'));
        } else {
          // Valor simples
          dadosValidos.valor_destinado = parseFloat(dadosValidos.valor_destinado);
        }
      }

      const response = await api.put(`/acao/${id}`, dadosValidos);
      logarSucesso('Atualizar', 'ação', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'ação');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'ação', { id });
    try {
      const response = await api.delete(`/acao/${id}`);
      logarSucesso('Excluir', 'ação', response.data);
      setDados(currentData => currentData.filter(item => item.id_acao !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'ação');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const colunas = useMemo(() => {
    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_acao, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.nome}"?`)) {
            excluirRegistro(data.id_acao);
          }
        },
        headerSort: false
      },
      {
        title: 'ID Ação',
        field: 'id_acao',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
        visible: false // Added to hide the column
      },
      {
        title: 'Nome',
        field: 'nome',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Valor destinado',
        field: 'valor_destinado',
        width: 180,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar valor...',
        sorter: 'number',
        formatter: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined || value === '') {
            return '';
          }
          return parseFloat(value).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          });
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = parseFloat(rowValue).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: function (cell, onRendered, success, cancel) {
          const input = document.createElement('input');
          input.type = 'text';
          input.style.width = '100%';
          input.style.padding = '4px';
          input.style.boxSizing = 'border-box';

          const currentValue = cell.getValue();
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            input.value = parseFloat(currentValue).toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }

          input.addEventListener('input', (e) => {
            let value = e.target.value.replace(/\D/g, '');
            if (value) {
              value = (parseInt(value) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              e.target.value = value;
            }
          });

          onRendered(() => {
            input.focus();
            input.select();
          });

          const successFunc = () => {
            const rawValue = input.value.replace(/\./g, '').replace(',', '.');
            success(parseFloat(rawValue) || 0);
          };

          input.addEventListener('blur', successFunc);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              successFunc();
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Data início',
        field: 'data_inicio',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          try {
            const date = new Date(rowValue);
            if (isNaN(date.getTime())) return rowValue.includes(headerValue);

            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            const formattedDate = `${day}/${month}/${year}`;

            return formattedDate.includes(headerValue);
          } catch (e) {
            return false;
          }
        },
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
      {
        title: 'Data término',
        field: 'data_termino',
        width: 180,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          try {
            const date = new Date(rowValue);
            if (isNaN(date.getTime())) return rowValue.includes(headerValue);

            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            const formattedDate = `${day}/${month}/${year}`;

            return formattedDate.includes(headerValue);
          } catch (e) {
            return false;
          }
        },
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/acao');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_acao'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, colunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { nome, valor_destinado, data_inicio, data_termino } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!nome) {
      camposObrigatorios.push('Nome');
      erros.push('nome');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      nome,
      valor_destinado: valor_destinado === '' ? null : parseFloat(valor_destinado.replace(/\./g, '').replace(',', '.')),
      data_inicio: data_inicio === '' ? null : data_inicio,
      data_termino: data_termino === '' ? null : data_termino,
    };

    logarRequisicao('Adicionar', 'ação', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/acao', dataToSend);
      logarSucesso('Adicionar', 'ação', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ nome: '', valor_destinado: '', data_inicio: '', data_termino: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'ação');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Formatação especial para valor_destinado
    if (name === 'valor_destinado') {
      // Remove tudo que não é número
      const apenasNumeros = value.replace(/\D/g, '');

      // Se não tem números, limpa o campo
      if (apenasNumeros === '') {
        setFormData(prev => ({ ...prev, [name]: '' }));
        return;
      }

      // Converte para número e formata com pontos e vírgula
      const numero = parseInt(apenasNumeros, 10);
      const valorFormatado = (numero / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      setFormData(prev => ({ ...prev, [name]: valorFormatado }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'acoes.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Ações</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_acao</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/acao.mov" buttonText="Ver Tutorial Ação" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="nome">Nome:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="nome" name="nome" value={formData.nome} onChange={handleInputChange} placeholder="Ex: Ação de Verão" maxLength={255} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('nome') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="valor_destinado">Valor destinado:</label>
            <input type="text" id="valor_destinado" name="valor_destinado" value={formData.valor_destinado} onChange={handleInputChange} placeholder="Ex: 1.000,00" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="data_inicio">Data início:</label>
            <input type="date" id="data_inicio" name="data_inicio" value={formData.data_inicio} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="data_termino">Data término:</label>
            <input type="date" id="data_termino" name="data_termino" value={formData.data_termino} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>

          <div className="actions-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', marginTop: '10px' }}>
            <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected' || !canCreateAcoes} title={!canCreateAcoes ? "Você precisa da permissão 'Cria ações' ativada no seu usuário." : ""}>
              {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
            </button>
            <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
              {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
            </button>
            <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            <div className="info-campos-obrigatorios" style={{ whiteSpace: 'nowrap' }}>
              <i className="fas fa-info-circle"></i>
              <span style={{ marginLeft: 6 }}>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default AcaoManager;
